import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2, Loader2 } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { INSURANCE_TYPES } from "@/lib/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputClass =
  "w-full rounded-xl border border-[#1BA8E0]/25 bg-[#E8F6FD]/40 px-4 py-3.5 text-sm text-[#2E3338] placeholder:text-[#2E3338]/40 outline-none transition-all duration-300 focus:border-[#1BA8E0] focus:bg-white focus:ring-4 focus:ring-[#1BA8E0]/15";

export const Contacto = ({ tipoSeguro, setTipoSeguro }) => {
  const [form, setForm] = useState({ nombre: "", telefono: "", correo: "", mensaje: "", acepta: false });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const update = (field) => (e) =>
    setForm((prev) => ({ ...prev, [field]: field === "acepta" ? e.target.checked : e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/cotizaciones`, {
        nombre: form.nombre,
        telefono: form.telefono,
        correo: form.correo,
        tipo_seguro: tipoSeguro,
        mensaje: form.mensaje,
        acepta_aviso: form.acepta,
      });
      setSent(true);
      toast.success("¡Gracias! Un asesor te contactará muy pronto.");
    } catch (err) {
      toast.error("No pudimos enviar tu solicitud. Escríbenos por WhatsApp y te ayudamos al momento.");
    } finally {
      setSending(false);
    }
  };

  const reset = () => {
    setForm({ nombre: "", telefono: "", correo: "", mensaje: "", acepta: false });
    setSent(false);
  };

  return (
    <section id="contacto" data-testid="contacto-section" className="relative overflow-hidden bg-[#1BA8E0] py-20 sm:py-28">
      <div className="pointer-events-none absolute -right-32 -top-32 h-96 w-96 rounded-full bg-white/10" aria-hidden="true" />
      <div className="pointer-events-none absolute -bottom-40 -left-24 h-[28rem] w-[28rem] rounded-full bg-[#0C5F8A]/20" aria-hidden="true" />

      <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
        <div className="grid items-center gap-12 lg:grid-cols-2 lg:gap-16">
          <Reveal>
            <h2 className="font-display text-2xl font-bold tracking-tight text-white sm:text-3xl lg:text-4xl">
              ¿Listo para proteger lo que más quieres?
            </h2>
            <p className="mt-5 max-w-md text-base leading-relaxed text-white/85 sm:text-lg">
              Déjanos tus datos y un asesor te contactará hoy mismo.
            </p>
            <ul className="mt-8 space-y-4">
              {["Cotización y asesoría sin costo", "Comparamos entre varias aseguradoras", "Te acompañamos antes, durante y después"].map((item) => (
                <li key={item} className="flex items-center gap-3 text-sm font-medium text-white/90 sm:text-base">
                  <span className="flex h-6 w-6 items-center justify-center rounded-full bg-[#F5B21B]">
                    <CheckCircle2 className="h-4 w-4 text-[#0C5F8A]" />
                  </span>
                  {item}
                </li>
              ))}
            </ul>
          </Reveal>

          <Reveal delay={0.12}>
            <div className="rounded-2xl bg-white p-7 shadow-[0_32px_70px_-20px_rgba(6,39,59,0.5)] sm:p-9">
              {sent ? (
                <div data-testid="form-success-message" className="flex flex-col items-center py-10 text-center">
                  <span className="flex h-20 w-20 items-center justify-center rounded-full bg-[#E8F6FD]">
                    <CheckCircle2 className="h-10 w-10 text-[#1BA8E0]" />
                  </span>
                  <h3 className="mt-6 font-display text-xl font-bold text-[#0C5F8A] sm:text-2xl">
                    ¡Gracias! Un asesor te contactará muy pronto.
                  </h3>
                  <p className="mt-3 text-sm text-[#2E3338]/70">
                    Recibimos tu solicitud de seguro de {tipoSeguro}.
                  </p>
                  <button
                    type="button"
                    onClick={reset}
                    data-testid="form-reset-button"
                    className="mt-8 rounded-full border-2 border-[#1BA8E0] px-7 py-2.5 font-display text-sm font-semibold text-[#1BA8E0] transition-colors hover:bg-[#E8F6FD]"
                  >
                    Enviar otra solicitud
                  </button>
                </div>
              ) : (
                <form onSubmit={onSubmit} data-testid="cotizacion-form" className="space-y-5">
                  <div className="grid gap-5 sm:grid-cols-2">
                    <div>
                      <label htmlFor="campo-nombre" className="mb-1.5 block font-display text-xs font-semibold uppercase tracking-wider text-[#0C5F8A]">
                        Nombre
                      </label>
                      <input
                        id="campo-nombre"
                        data-testid="input-nombre"
                        type="text"
                        required
                        minLength={2}
                        placeholder="Tu nombre completo"
                        value={form.nombre}
                        onChange={update("nombre")}
                        className={inputClass}
                      />
                    </div>
                    <div>
                      <label htmlFor="campo-telefono" className="mb-1.5 block font-display text-xs font-semibold uppercase tracking-wider text-[#0C5F8A]">
                        Teléfono / WhatsApp
                      </label>
                      <input
                        id="campo-telefono"
                        data-testid="input-telefono"
                        type="tel"
                        required
                        minLength={8}
                        placeholder="55 1234 5678"
                        value={form.telefono}
                        onChange={update("telefono")}
                        className={inputClass}
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="campo-correo" className="mb-1.5 block font-display text-xs font-semibold uppercase tracking-wider text-[#0C5F8A]">
                      Correo
                    </label>
                    <input
                      id="campo-correo"
                      data-testid="input-correo"
                      type="email"
                      required
                      placeholder="tucorreo@ejemplo.com"
                      value={form.correo}
                      onChange={update("correo")}
                      className={inputClass}
                    />
                  </div>

                  <div>
                    <label htmlFor="campo-tipo" className="mb-1.5 block font-display text-xs font-semibold uppercase tracking-wider text-[#0C5F8A]">
                      Tipo de seguro
                    </label>
                    <select
                      id="campo-tipo"
                      data-testid="select-tipo-seguro"
                      required
                      value={tipoSeguro}
                      onChange={(e) => setTipoSeguro(e.target.value)}
                      className={`${inputClass} cursor-pointer`}
                    >
                      {INSURANCE_TYPES.map((tipo) => (
                        <option key={tipo} value={tipo}>
                          {tipo}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label htmlFor="campo-mensaje" className="mb-1.5 block font-display text-xs font-semibold uppercase tracking-wider text-[#0C5F8A]">
                      Mensaje
                    </label>
                    <textarea
                      id="campo-mensaje"
                      data-testid="input-mensaje"
                      rows={3}
                      placeholder="Cuéntanos qué quieres proteger…"
                      value={form.mensaje}
                      onChange={update("mensaje")}
                      className={`${inputClass} resize-none`}
                    />
                  </div>

                  <label htmlFor="campo-aviso" className="flex cursor-pointer items-start gap-3 text-xs leading-relaxed text-[#2E3338]/75 sm:text-sm">
                    <input
                      id="campo-aviso"
                      data-testid="checkbox-aviso"
                      type="checkbox"
                      required
                      checked={form.acepta}
                      onChange={update("acepta")}
                      className="mt-0.5 h-4 w-4 shrink-0 cursor-pointer accent-[#F5B21B]"
                    />
                    <span>
                      Acepto el{" "}
                      <Link to="/aviso-de-privacidad" data-testid="link-aviso-privacidad" className="font-semibold text-[#1BA8E0] underline underline-offset-2 hover:text-[#0C5F8A]">
                        Aviso de Privacidad
                      </Link>
                    </span>
                  </label>

                  <button
                    type="submit"
                    disabled={sending}
                    data-testid="form-submit-button"
                    className="flex w-full items-center justify-center gap-2 rounded-full bg-[#F5B21B] py-4 font-display text-base font-semibold text-[#0C5F8A] shadow-[0_14px_30px_-8px_rgba(245,178,27,0.7)] transition-all duration-300 hover:-translate-y-0.5 hover:bg-[#E0A012] disabled:cursor-not-allowed disabled:opacity-70 disabled:hover:translate-y-0"
                  >
                    {sending && <Loader2 className="h-5 w-5 animate-spin" />}
                    {sending ? "Enviando…" : "Quiero mi cotización"}
                  </button>
                </form>
              )}
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
